import classNames from 'classnames';
import { includes } from 'ramda';
import React, { useEffect, useState } from 'react';
import { adjustInactiveColors, toggleItem } from 'src/js/helper/chart-helper';
import { StatsInterval } from 'src/js/model/api/request/stream-stat-request';
import { mapToVictoryData, mapToVictoryInMinutes } from 'src/js/pages/statistic/statistic-mapper';
import { axisTitleStyle, defaultChartColors, horizontalAxisStyle, verticalAxisStyle } from 'src/js/theme/chart';
import { ChartViewModel } from 'src/js/view-model/chart-view-model';
import { VictoryAxis, VictoryChart, VictoryGroup, VictoryLabel, VictoryLine, VictoryVoronoiContainer } from 'victory';

import { LegendHorizontal } from './legend-horizontal';
import { generateDataAuto } from 'src/js/util/automation';

/*
 * Line Chart component for statistics page
 *
 * data: StatsChartViewModel
 * title: Chart title
 * subTitle: Chart subtitle (Used on dashboard)
 * fontSize: size of font for x/y axis labels
 * yTickCount: number of ticks on the y axis
 * padding: padding around graph
 * yFields: fields to render from the data
 * height: height of chart in pixels
 * width: width of chart in pixels
 * xLabel: x axis label
 * yLabel: y axis label
 * showYLabels: whether to show y axis labels
 * chartProps: additional chart props conforming to VictoryThemeDefinition
 * showLegend: whether to display or hide the legend
 *
 */

interface MkAxisRange {
  limit: number;
  divideBy: number;
  label: string;
}

interface LineChartProps {
  data: ChartViewModel[];
  title?: string;
  subTitle?: string;
  fontSize?: number;
  xTickCount?: number;
  yTickCount?: number;
  padding?: any;
  timescale: StatsInterval;
  xAxisMinutes?: boolean;
  height?: number;
  width?: number;
  xLabel?: string;
  yLabel?: string;
  yTitle?: string;
  units?: string;
  pathSelected?: string[];
  showLegend?: boolean;
  showRedundancy?: boolean;
  dashboard?: boolean;
  lineColors: string[];
  defaultInactiveFields?: string[];
  tooltips?: boolean;
}

export const LineChart: React.FunctionComponent<LineChartProps> = ({
  data,
  title,
  subTitle,
  fontSize,
  xTickCount = 5,
  yTickCount = 5,
  padding = { top: 21, bottom: 16, right: 0, left: 50 },
  timescale,
  xAxisMinutes = false,
  height,
  width,
  yLabel,
  xLabel,
  yTitle,
  units,
  pathSelected = ['path1', 'path2'],
  showLegend = true,
  showRedundancy = false,
  dashboard = false,
  lineColors = defaultChartColors,
  defaultInactiveFields = [],
  tooltips = true,
  ...rest
}) => {
  const yFieldsPath1 = data.map((d) => d.name).filter((n) => !n.endsWith('_Path2'));
  const yFieldsPath2 = data.map((d) => d.name).filter((n) => n.endsWith('_Path2'));

  const [inactiveFields, setInactiveFields] = useState<string[]>(defaultInactiveFields);
  const [inactivePathFields, setInactivePathFields] = useState<string[]>([]);
  // console.log('inactive fields', inactiveFields, inactivePathFields);

  useEffect(() => {
    setInactivePathFields((inactiveFields) => {
      if (pathSelected.includes('path1')) {
        // remove all path1 from inactive fields
        inactiveFields = inactiveFields.filter((f) => !yFieldsPath1.includes(f));
      } else {
        // add all path 1 to inactive fields
        inactiveFields = inactiveFields.concat(yFieldsPath1);
      }

      if (pathSelected.includes('path2')) {
        // remove all path 2 from inactive fields
        inactiveFields = inactiveFields.filter((f) => !yFieldsPath2.includes(f));
      } else {
        // add all path 2 to inactive fields
        inactiveFields = inactiveFields.concat(yFieldsPath2);
      }
      return inactiveFields;
    });
  }, [pathSelected]);

  let visibleData = data.filter((chartViewModel: ChartViewModel) => !includes(chartViewModel.name, inactiveFields));
  visibleData = visibleData.filter(
    (chartViewModel: ChartViewModel) => !includes(chartViewModel.name, inactivePathFields),
  ); // filter out unselected path as per path selection dropdown

  let maxYvalue = 0;
  const targetRange: MkAxisRange = null;
  const series = visibleData.map((viewModels) => {
    viewModels.values.map((yPoint) => {
      if (yPoint.value > maxYvalue) {
        maxYvalue = yPoint.value;
      }
    });

    return {
      data: xAxisMinutes
        ? mapToVictoryInMinutes(viewModels.values, viewModels.label)
        : mapToVictoryData(viewModels.values, timescale, viewModels.label),
      name: viewModels.name,
      style: viewModels.name.includes('_Path2') ? { data: { strokeDasharray: '5,3' } } : undefined,
    };
  });

  // filter out all _Path2 from data to get the line y-values names & labels (Latency, RTT, etc..)
  const yFields = data.filter((chartViewModel) => !showRedundancy || !chartViewModel.name.endsWith('_Path2'));

  const yFieldNames = yFields.map((chartViewModel) => chartViewModel.name);
  //const yFieldLabels = yFields.map((chartViewModel) => chartViewModel.label);

  // Map legend labels from array to object
  const legendItem = yFields.map((yField) => ({ label: yField.label, value: yField.name }));

  const adjustedColorScale = adjustInactiveColors(yFieldNames, inactiveFields, lineColors); // FIXME also add inactive from path selections
  const numberWithCommas = (x: number) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  let yScale = 'K'; // 999 kbps and less
  if (maxYvalue >= 100000) {
    yScale = '10M'; // 10,000 kbps +
  } else if (maxYvalue >= 10000) {
    yScale = 'M'; // 1,000 kbps +
  }

  /* NKE-2421 to make large labels fit */
  if (yScale === '10M') {
    padding.left = 70;
  } else if (yScale === 'M') {
    padding.left = 60;
  } else {
    padding.left = 53;
  }
  if (dashboard) {
    padding.left += 11; // NKE-2469 dashboard have smaller graphs and axis labels need more reserved space
  }
  // console.log('padding left', yScale, padding.left, maxYvalue)
  return (
    <div
      className={classNames('mk-chart-container', dashboard && 'dashboard')}
      data-auto={generateDataAuto('statistics', 'graph')}
      {...rest}
    >
      {title && (
        <div className="d-flex justify-content-center statistics-header hai-mb-3" title={title}>
          {title}
        </div>
      )}
      {subTitle && (
        <div className="statistics-graph-subheader" title={subTitle}>
          {subTitle}
        </div>
      )}
      <div>
        <VictoryChart
          domainPadding={{ x: 10, y: 15 }}
          height={height}
          width={width}
          padding={padding}
          containerComponent={
            tooltips ? (
              <VictoryVoronoiContainer
                labels={({ datum }) => {
                  // tooltip graph
                  // console.log('datum', datum);
                  const amountOfSeries = lineColors?.length;
                  if (amountOfSeries === 1) {
                    // dont print axis label since there is only one
                    if (Math.round(datum.y as number) == 0 && datum.y > 0 && datum.yOriginal > 0) {
                      return `${Math.round(datum.yOriginal as number)} ${units ?? yTitle}`;
                    }
                    if (units != null || yTitle != null) {
                      return `    ${Math.round(datum.y as number)} ${
                        targetRange ? targetRange.label : units ?? yTitle
                      }`;
                    }
                    return `    ${Math.round(datum.y as number)} ${targetRange ? targetRange.label : ''}`;
                  }

                  if (Math.round(datum.y as number) == 0 && datum.y > 0 && datum.yOriginal > 0) {
                    return `    ${datum.lineName as string} ${Math.round(datum.yOriginal as number)} ${
                      units ?? yTitle
                    }`; // TODO we could still improve by accessing other targetRanges instead of going to the default one
                  }
                  let value;
                  if (datum.y >= 9.9) {
                    value = Math.round(datum.y as number);
                  } else {
                    value = Math.round((datum.y * 100) as number) / 100;
                  }
                  if (units != null || yTitle != null) {
                    return `    ${datum.lineName as string} ${value} ${
                      targetRange ? targetRange.label : units ?? yTitle
                    }`;
                  }
                  return `    ${datum.lineName as string} ${value} ${targetRange ? targetRange.label : ''}`;
                }}
              />
            ) : undefined
          }
        >
          <VictoryGroup colorScale={adjustedColorScale}>
            {series.map((point, index: number) => (
              <VictoryLine data={point.data} key={`${data[index].name}-${index}`} style={point.style} />
            ))}
          </VictoryGroup>
          {yTitle && (
            <VictoryLabel
              x={47}
              y={8}
              text={targetRange ? targetRange.label : yTitle}
              style={axisTitleStyle}
              textAnchor="end"
            />
          )}
          <VictoryAxis
            dependentAxis
            label={yLabel}
            style={{
              ...verticalAxisStyle,
              tickLabels: {
                ...verticalAxisStyle.tickLabels,
                fontSize: fontSize ? fontSize : verticalAxisStyle.tickLabels.fontSize,
              },
            }}
            // We round to have 3 decimals maximum, no need for more precision, it makes the graphs look odd
            // We use the `Math.round` to not add the trailing .0 if not needed
            tickFormat={(tick) => {
              const value = Math.round(Number(tick) * 1000) / 1000;
              if (dashboard) {
                return value;
              }
              return numberWithCommas(value);
            }}
            tickCount={yTickCount}
          />
          <VictoryAxis
            style={{
              ...horizontalAxisStyle,
              tickLabels: {
                ...horizontalAxisStyle.tickLabels,
                fontSize: fontSize ? fontSize : horizontalAxisStyle.tickLabels.fontSize,
              },
            }}
            tickCount={xTickCount}
            padding={{ left: 20, bottom: 20, top: 20 }}
          />
        </VictoryChart>
      </div>
      {showLegend && (
        <LegendHorizontal
          handleClick={(item) => {
            // one legend item controls both path on/off
            setInactiveFields((inactiveFields) => toggleItem(item, inactiveFields));
            setInactiveFields((inactiveFields) => toggleItem(item + '_Path2', inactiveFields));
          }}
          items={legendItem}
          colorScale={lineColors}
          inactiveItems={inactiveFields}
        />
      )}
    </div>
  );
};
